import React, { ReactElement, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { OutletContext } from '../../layout/Layout';
import { AlertVariant, Modal, ModalVariant } from '@patternfly/react-core';
import ZiTable, { Action, Column } from '../../components/table/ZiTable';
import FilterTableLayout from '../../layout/FilterTableLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import SchnurForm, { Field, UIType } from '../../components/form/SchnurForm/SchnurForm';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import { Permission } from '../../enums/permission.enum';
import PermissionButton from '../../components/button/PermissionButton';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { Tag, TTag } from '../../api/tags/Tags';
import { QuestionTag, TQuestionTag } from '../../api/tags/QuestionTags';

export default function QuestionTags(): ReactElement {
	const { addToast } = useToast();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
	const [data, setData] = useState<TTag[]>([]);
	const [linkedQuestionTags, setLinkedQuestionTags] = useState<TQuestionTag[]>([]);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [activeTag, setActiveTag] = useState<TTag>(Tag.Default());
	const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
	const [tableColumns, setTableColumns] = useState<Column<TTag>[]>([]);

	const actions: Action<TTag>[] = [
		{
			name: (
				<>
					<FontAwesomeIcon icon={faPenToSquare} />
					Edit
				</>
			),
			callback: (item) => {
				setActiveTag(item);
				setIsModalOpen(true);
			},
			permission: Permission.EditTag,
		},
		{
			name: (
				<>
					<FontAwesomeIcon icon={faTrashAlt} />
					Delete
				</>
			),
			callback: (item) => {
				setTableLoading(true);
				Tag.Delete(item.id!)
					.then(() => {
						setTableLoading(false);
						setData((prev) => prev.filter((dimension) => dimension.id !== item.id));
						addToast('Question Tag deleted successfully.', AlertVariant.success);
					})
					.catch(() => {
						setTableLoading(false);
						addToast(
							'An error occurred while trying to delete Question Tag. Please try again later.',
							AlertVariant.danger
						);
					});
			},
			permission: Permission.DeleteTag,
		},
	];

	const getAllTags = () => {
		Tag.GetAll()
			.then((tags) => {
				setData(tags.sort((a, b) => a.name.localeCompare(b.name)));
				setTableLoading(false);
			})
			.catch(() => {
				addToast(
					'An error occurred while trying to load question tags. Please try again later.',
					AlertVariant.danger
				);
			});
		void QuestionTag.GetAll().then((response) => {
			setLinkedQuestionTags(response);
		});
	};

	useEffect(() => {
		const selectedColumns: Column<TTag>[] = [
			{
				title: 'Name',
				columnName: 'name',
				sortable: true,
			},
			{
				title: '# of Questions using Tag',
				columnName: 'id',
				sortable: true,
				customAccessor(row) {
					const number = linkedQuestionTags.filter((x) => x.tag == row.id).length;
					return number;
				},
			},
		];
		setTableColumns(selectedColumns);
	}, [linkedQuestionTags]);

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle="Question Tags"
					pageDescription="Manage your Question Tags."
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
				/>
			),
		});

		getAllTags();
	}, [setSubSide, subNavExpanded, setSubNavExpanded, isModalOpen]);

	useEffect(() => {
		if (!isModalOpen) {
			setActiveTag(Tag.Default());
		}
	}, [isModalOpen]);

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const handleSuccess = () => {
		getAllTags();
		setIsModalOpen(false);
	};

	const addButton = (
		<PermissionButton
			data-testid={'question-tag-add-button'}
			variant={'primary'}
			permission={Permission.CreateTag}
			onClick={() => {
				setActiveTag(Tag.Default());
				setIsModalOpen(true);
			}}
		>
			New Question Tag
		</PermissionButton>
	);

	const formProperties: Field<TTag>[] = [
		{
			title: 'Name',
			columnName: 'name',
			uiSchema: {
				type: UIType.TEXT,
			},
			required: true,
		},
	];

	const questionTagTable = (
		<ZiTable<TTag>
			ariaLabel={'Question Tags Table'}
			columns={tableColumns}
			data={data}
			caption="Question Tags"
			actions={actions}
			loading={tableLoading}
		/>
	);

	return (
		<React.Fragment>
			<Modal
				variant={ModalVariant.medium}
				title="Question Tags Management"
				isOpen={isModalOpen}
				onClose={handleClose}
			>
				<SchnurForm<TTag>
					title={'Question Tags'}
					fields={formProperties}
					initialSubject={activeTag}
					isLoading={isFormLoading}
					onSubmit={(tag) => {
						setIsFormLoading(true);
						if ('id' in tag && tag.id != 0) {
							Tag.Update(tag)
								.then(() => {
									handleSuccess();
									addToast(
										'Question Tag succesfully updated',
										AlertVariant.success
									);
								})
								.catch(() => {
									addToast(
										'An error occurred while trying to save the question tag. Please try again later.',
										AlertVariant.danger
									);
								})
								.finally(() => {
									setIsFormLoading(false);
								});
						} else {
							tag.id = undefined;
							Tag.New(tag)
								.then(() => {
									handleSuccess();
									addToast(
										'Question Tag succesfully added',
										AlertVariant.success
									);
								})
								.catch(() => {
									addToast(
										'An error occurred while trying to save the question tag. Please try again later.',
										AlertVariant.danger
									);
								})
								.finally(() => {
									setIsFormLoading(false);
								});
						}
					}}
				/>
			</Modal>
			<React.Fragment>
				<FilterTableLayout
					table={questionTagTable}
					layoutActions={[addButton]}
				/>
			</React.Fragment>
		</React.Fragment>
	);
}
